import {
    useRef,
    useState
} from 'react';

import {SimpleGrid} from '@mantine/core';

import {usePrices} from '../hooks/usePrices.ts';
import {usePricesSelection} from '../hooks/usePricesSelection.ts';
import {useTableColumns} from '../hooks/useTableColumns.tsx';
import {FormDrawer} from '../drawers/FormDrawer.tsx';
import {update} from '../services/PricesService.ts';

import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableTokenPagination} from "../../../components/DataTableTokenPagination.tsx";
import {Price} from '../types';
import {useQueryClient} from '@tanstack/react-query';

interface RecordsTableProps {
    productId?: string;
}

export const RecordsTable = ({productId}: RecordsTableProps) => {
    const {
        selectedRecords,
        setSelectedRecords
    } = usePricesSelection();
    const {columns} = useTableColumns();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Price | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = usePrices(productId);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleDrawerSubmit = async (values: Price) => {
        if (selectedRecord?.id) {
            const {created: _created, updated: _updated, createdByUsername: _createdByUsername, createdBy: _createdBy, product: _product, ratePlan: _ratePlan, ...rest} = values;
            await update(selectedRecord.id, rest as Price);
            queryClient.invalidateQueries({queryKey: ['prices', productId]});
        }
    };

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                isRecordSelectable={(record) => record.priceStatus === 'Active'}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
                onRowClick={({record}) => {
                    setSelectedRecord(record);
                    setDrawerOpened(true);
                }}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken}
                                      handleLoadMore={fetchRecords}/>

            <FormDrawer
                record={selectedRecord || {} as Price}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
